import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import type { GetPriceListDownloadUrlInput } from "@/graphql/codegen/graphql"
import { useFileDownload } from "@/hooks/useFileDownload"
import { DownloadPriceListForm } from "@/screens/Companies/components/forms/DownloadPriceListForm"
import { useGetDownloadPriceList } from "@/screens/Companies/hooks/useGetDownloadPriceList"
import type { DownloadPriceListSchemaSchemaFormValues } from "@/screens/Companies/schemas/DownloadPriceListSchema"
import { DownloadPriceListSchema } from "@/screens/Companies/schemas/DownloadPriceListSchema"

interface SubmitPricingDialogProps {
  companyId: string
  onClose: () => void
  onSuccess: () => void
}

const DownloadPriceListDialog: FC<SubmitPricingDialogProps> = ({ companyId, onSuccess, onClose }) => {
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const { getDownloadUrl } = useGetDownloadPriceList()
  const { downloadFile } = useFileDownload()

  const methods = useForm<GetPriceListDownloadUrlInput>({
    defaultValues: {
      effectiveDate: "",
    },
    mode: "onChange",
    resolver: zodResolver(DownloadPriceListSchema),
  })

  const {
    formState: { isDirty, isValid },
  } = methods

  const confirmCancel = () => {
    onClose()
  }

  const handleCancel = () => {
    setIsCancelConfirmationOpen(true)
  }

  const handleSubmit = async (data: DownloadPriceListSchemaSchemaFormValues) => {
    const downloadUrl = await getDownloadUrl({
      companyId,
      effectiveDate: data.effectiveDate as string,
    })

    if (!downloadUrl) {
      return
    }

    await downloadFile(downloadUrl, "price-change-" + data.effectiveDate)

    onSuccess()
  }

  return (
    <>
      {isCancelConfirmationOpen && (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={confirmCancel} />
      )}
      <Dialog open onClose={handleCancel} fullWidth>
        <>
          <DialogTitle className='pl-10 font-normal text-primary'>Download Price List</DialogTitle>
          <IconButton aria-label='close' onClick={handleCancel} className='absolute right-4 top-4 text-primary'>
            <CloseOutlined />
          </IconButton>
          <Divider />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...methods}>
              <DownloadPriceListForm />
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button appearance='outlined' onClick={handleCancel}>
              Cancel
            </Button>
            <Button disabled={!isValid || !isDirty} onClick={methods.handleSubmit(handleSubmit)}>
              Download Price List
            </Button>
          </DialogActions>
        </>
      </Dialog>
    </>
  )
}

export default DownloadPriceListDialog
